import throttle from 'lodash/throttle'
import { isScrolledPast } from 'Helpers'

const STICKY_HIDDEN = 'Cta-sticky--hidden'
const STICKY_SHOWN = 'Cta-sticky--shown'
const HAS_STICKY_CTA_CLASS = 'has-sticky-cta'
const STICKY_CTA_HEIGHT_VAR = '--sticky-cta-height'

const serviceWithFeePageCTA = '.Template-service-with-fee-ppc div.ServiceHeader div.Container > div.CtaOptions a.Button'
const servicePrescriptionPageCTA = '.Template-service-prescription-ppc div.ServiceHeader div.Container > div.CtaOptions a.Button'
const servicePageCTA = '.Template-service .ServiceHeader div.CtaOptions a.Button'
const servicePageHeroVariantCTA = '.Template-service .HeroVariant div.CtaOptions a.Button'
const productPageCTA = 'main div.Container  > div.Product-wrap   div.CtaOptions a.Button'
const productPageIECTA = 'main div.CTA div.Container > a.Button'
const alternativeCTA = 'div.App-cta-container div.App-cta-content a.Button'
const appCTAContainer = 'div.App-cta-container div.App-cta-content a.Button'
const contentHeaderWrapper = '.ContentHeaderWrapper'
const blockGenericHeroCTA = 'body[class^=Template-block-] .GenericHero__ctaContainer > a.button'
const blockProductHeroCTA = 'body[class^=Template-block-] .ProductHero__ctaContainer a.button'
const blockDiscountHeroCTA = 'body[class^=Template-block-] .DiscountHero-container'
const blockCTAButton = 'body[class^=Template-block-] .CTAButtonComponent'

const watchedElem = ((...watchedCandidates) => {
  return watchedCandidates.find(watchedSelector => !!document.querySelector(watchedSelector))
})(
  alternativeCTA,
  appCTAContainer,
  contentHeaderWrapper,
  productPageCTA,
  productPageIECTA,
  serviceWithFeePageCTA,
  servicePrescriptionPageCTA,
  servicePageCTA,
  servicePageHeroVariantCTA,
  blockGenericHeroCTA,
  blockProductHeroCTA,
  blockDiscountHeroCTA,
  blockCTAButton
)
const ctaSticky = '.Cta-sticky'

export default class StickyCTA {
  /**
     * Creates an instance of StickyCTA.
     *
     * @memberof StickyCTA
     */
  constructor (watchedElement, selector = ctaSticky, scrollOffset = 2, hiddenClass = STICKY_HIDDEN, visibleClass = STICKY_SHOWN) {
    this.hiddenClass = hiddenClass
    this.visibleClass = visibleClass
    this.watchedElement = watchedElement
    this.scrollOffset = scrollOffset
    this.selector = selector

    // Bind context to event handlers.
    this.throttledHandleScroll = throttle(this.handleScroll.bind(this), 500)

    // Kicking off the component.
    this.init()
  }

  /**
     * Sets scroll event listener.
     *
     * @memberof StickyCTA
     */
  init () {
    window.addEventListener('scroll', this.throttledHandleScroll)
    this.syncStickyHeight()
    window.addEventListener('resize', this.throttledHandleScroll)
  }

  /**
     * Removes scroll event listener.
     *
     * @memberof StickyCTA
     */
  destroy () {
    window.removeEventListener('scroll', this.throttledHandleScroll)
    window.removeEventListener('resize', this.throttledHandleScroll)
  }

  syncStickyHeight () {
    const stickyCta = document.querySelector(this.selector)

    if (!stickyCta) {
      document.body.classList.remove(HAS_STICKY_CTA_CLASS)
      document.documentElement.style.removeProperty(STICKY_CTA_HEIGHT_VAR)
      return
    }

    // Expose sticky CTA presence + height so footer can reserve enough space and avoid overlap.
    document.body.classList.add(HAS_STICKY_CTA_CLASS)
    document.documentElement.style.setProperty(STICKY_CTA_HEIGHT_VAR, `${stickyCta.offsetHeight}px`)
  }

  handleScroll () {
    const scrolledPast = isScrolledPast(this.watchedElement, this.scrollOffset)
    const stickyCta = document.querySelector(this.selector)

    this.syncStickyHeight()

    if (stickyCta && !scrolledPast && stickyCta.classList.contains(this.visibleClass)) {
      stickyCta.setAttribute('aria-hidden', 'true')
      stickyCta.setAttribute('inert', '')
      this.disableFocusableElements(stickyCta)
      stickyCta.classList.add(this.hiddenClass)
      stickyCta.classList.remove(this.visibleClass)
    } else if (stickyCta && scrolledPast && stickyCta.classList.contains(this.hiddenClass)) {
      stickyCta.setAttribute('aria-hidden', 'false')
      stickyCta.removeAttribute('inert')
      this.enableFocusableElements(stickyCta)
      stickyCta.classList.add(this.visibleClass)
      stickyCta.classList.remove(this.hiddenClass)
    }
  }

  disableFocusableElements (container) {
    const focusableSelectors = 'a, button, [tabindex]:not([tabindex="-1"])'
    const focusableElements = container.querySelectorAll(focusableSelectors)
    focusableElements.forEach(el => {
      if (el.getAttribute('tabindex')) {
        el.dataset.stickyCTAOriginalTabindex = el.getAttribute('tabindex')
      }
      el.setAttribute('tabindex', '-1')
    })
  }

  enableFocusableElements (container) {
    const disabledElements = container.querySelectorAll('[tabindex="-1"]')
    disabledElements.forEach(el => {
      if (el.dataset.stickyCTAOriginalTabindex) {
        el.setAttribute('tabindex', el.dataset.stickyCTAOriginalTabindex)
        delete el.dataset.stickyCTAOriginalTabindex
      } else {
        el.removeAttribute('tabindex')
      }
    })
  }
}

// There can only be one sticky CTA per page so creating object here as its simple
if (watchedElem) {
  new StickyCTA(watchedElem)
} else {
  console.warn('StickyCTA: no watched element found; sticky CTA was not initialized.')
}
